import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { AppLoadingSkeleton } from "./AppLoadingSkeleton";
import {
    CHUNK_BACKOFF_ATTEMPT_KEY,
    isChunkLoadError,
    refreshFailedChunkCache,
    reloadForFreshChunks,
    stripChunkCacheBustSearchParam,
} from "../utils/retryLazyImport";

/** Gaps between background retries once the immediate reloads are spent (mid-deploy / CDN 404 window). */
const BACKOFF_DELAYS_MS = [3_000, 10_000, 20_000, 30_000, 60_000];

function errorFromRoute(error: unknown): unknown {
    if (isRouteErrorResponse(error)) return error.data ?? error.statusText ?? error;
    return error;
}

function readBackoffAttempt(): number {
    try {
        return parseInt(sessionStorage.getItem(CHUNK_BACKOFF_ATTEMPT_KEY) || "0", 10) || 0;
    } catch {
        return 0;
    }
}

function cacheBustedReload(): void {
    const url = new URL(window.location.href);
    stripChunkCacheBustSearchParam(url.searchParams);
    url.searchParams.set("_cb", String(Date.now()));
    window.location.replace(url.toString());
}

/**
 * Route error element. Stale/missing lazy chunks (tab open across a deploy) never show an error:
 * reload immediately (shared budget), then keep retrying in the background with growing delays.
 * The counter resets once the app stays up (`clearChunkReloadSessionFlags` in Layout).
 */
export function ChunkLoadRecovery() {
    const routeError = useRouteError();
    const error = errorFromRoute(routeError);
    const isChunk = isChunkLoadError(error);

    useEffect(() => {
        if (!isChunk || typeof window === "undefined") return;
        let cancelled = false;
        let timerId: number | undefined;

        void refreshFailedChunkCache(error).finally(() => {
            if (cancelled) return;
            if (reloadForFreshChunks()) return;

            const attempt = readBackoffAttempt();
            const delay = BACKOFF_DELAYS_MS[Math.min(attempt, BACKOFF_DELAYS_MS.length - 1)];
            timerId = window.setTimeout(() => {
                try {
                    sessionStorage.setItem(CHUNK_BACKOFF_ATTEMPT_KEY, String(attempt + 1));
                } catch {
                    /* ignore */
                }
                cacheBustedReload();
            }, delay);
        });

        return () => {
            cancelled = true;
            if (timerId != null) window.clearTimeout(timerId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per error screen
    }, [isChunk]);

    if (isChunk) {
        return <AppLoadingSkeleton />;
    }

    const message =
        error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "Something went wrong.";

    return (
        <div className="flex min-h-dvh flex-col items-center justify-center bg-[#1B062F] px-6 text-center text-white">
            <p className="font-anton text-[22px] uppercase tracking-wide text-white/90">Something went wrong</p>
            <p className="mt-3 max-w-lg font-chakra text-[14px] leading-relaxed text-white/70">{message}</p>
            <button
                type="button"
                className="mt-6 border-0 bg-lime px-5 py-3 font-anton text-[14px] uppercase text-[#1B062F]"
                onClick={() => window.location.reload()}
            >
                Refresh
            </button>
        </div>
    );
}
